/*
 * Regios <-> Nosto price bridge (DRAFT, test on an unpublished theme first)
 *
 * Re-applies Regios promo prices to Nosto-rendered collection and search tiles.
 * Loaded from sections/nosto-collection.liquid and sections/nosto-search.liquid.
 * Nosto renders Broadcast's own card (.product-item) inside
 * <nosto-dynamic-card> elements, which fill in after page load, so this
 * keeps watching the grid and updates each card once its content arrives.
 * If Nosto later rewrites a card's price, that card is updated again.
 *
 * Needs templates/product.regios-data.liquid (tags + collection IDs),
 * so it works for tag-based and collection-based Regios promos.
 *
 * Field names and page types follow Regios's dopp_public_api.d.ts.
 * Optional: swap the MutationObserver for Nosto's render-complete event.
 */
(function () {
  const ROOT_SELECTOR = '#serp'; // Nosto's mount point, present on page load
  const CARD_SELECTOR = '.product-item';
  const PRICE_SELECTOR = '.product-item__price';
  const REVEAL_TIMEOUT_MS = 3000;

  // Page type for Regios, from the Nosto tag in nosto-collection / nosto-search.
  // Values from Regios's DOPPPublicApiExtensionType (dopp_public_api.d.ts).
  const nostoPage = (document.querySelector('.nosto_page_type') || {}).textContent;
  const PAGE_TYPE = nostoPage && nostoPage.trim() === 'search' ? 'SEARCH_PAGE' : 'COLLECTION_PAGE';

  const root = document.querySelector(ROOT_SELECTOR);
  if (!root) return;

  // Hide only the price of cards being updated, so the full price doesn't flash
  const style = document.createElement('style');
  style.textContent =
    '.regios-pending ' + PRICE_SELECTOR + ', .regios-pending .product-item__promo-message { visibility: hidden; }';
  document.head.appendChild(style);

  // 1. Regios API: ready now, or wait for its init event
  const apiReady = new Promise((resolve) => {
    const api = window.RegiosDOPP && window.RegiosDOPP.api && window.RegiosDOPP.api.v0;
    if (api) return resolve(api);
    window.addEventListener('regios-dopp:api-initialized', (e) => resolve(e.detail.api), { once: true });
  });

  // 2. Product data from the alternate product view, cached per handle
  const cache = new Map();
  function handleFromCard(card) {
    const href = card.getAttribute('data-url') ||
      (card.querySelector('a[href*="/products/"]') || {}).getAttribute?.('href');
    if (!href) return null;
    const part = new URL(href, location.origin).pathname.split('/products/')[1];
    return part ? part.split(/[?#/]/)[0] : null;
  }
  function productFor(handle) {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch('/products/' + handle + '?view=regios-data')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }
    return cache.get(handle);
  }

  // A card needs updating if its price has changed since Regios last ran on it
  // (new card, card content replaced, or price rewritten by Nosto)
  const priceText = (card) => {
    const el = card.querySelector(PRICE_SELECTOR);
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : null;
  };
  const needsUpdate = (card) =>
    priceText(card) !== null && card.dataset.regiosPrice !== priceText(card);

  // 3. Update the cards that need it
  let running = false;
  let again = false;
  async function refresh() {
    if (running) { again = true; return; }
    running = true;
    try {
      const cards = [...root.querySelectorAll(CARD_SELECTOR)].filter(needsUpdate);
      if (!cards.length) return;

      cards.forEach((c) => c.classList.add('regios-pending'));
      const reveal = setTimeout(
        () => cards.forEach((c) => c.classList.remove('regios-pending')),
        REVEAL_TIMEOUT_MS
      );
      try {
        const api = await apiReady;
        const handles = [...new Set(cards.map(handleFromCard).filter(Boolean))];
        const products = (await Promise.all(handles.map(productFor))).filter(Boolean);
        const cells = await api.themeInterop.findProductGridCellsByUrl(root, products, [CARD_SELECTOR]);
        for (const cell of cells) {
          if (!cards.includes(cell.element)) continue;
          await api.themeInterop.updateOnPagePrices(cell.element, PAGE_TYPE, cell.product);
        }
      } catch (err) {
        console.warn('[Regios-Nosto bridge]', err); // original prices are revealed below
      } finally {
        clearTimeout(reveal);
        // Record the price as it stands now, so Regios's own change doesn't retrigger
        cards.forEach((c) => {
          c.dataset.regiosPrice = priceText(c) || '';
          c.classList.remove('regios-pending');
        });
      }
    } finally {
      running = false;
      if (again) { again = false; refresh(); }
    }
  }

  // 4. Run whenever Nosto adds or changes cards (debounced)
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 100);
  }).observe(root, { childList: true, subtree: true, characterData: true });
  refresh();
})();